<template>
  <div data-testid="site-leads-page">
    <!-- Leads are never created or edited here: they arrive from the
         institutional site's signed webhook and the panel is a read-only log.
         So there is no add button and no save anywhere on this page. -->
    <div class="site-leads-toolbar" data-testid="site-leads-filters">
      <div class="site-leads-toolbar-field">
        <select-list
          elem-id="siteLeadsFiltroFormulario"
          v-model="filtroFormulario"
          :label="t('Formulário')"
          :itens-select="formularioOptions"
          :auto-select-first="false"
          data-testid="site-leads-formulario-filter"
        />
      </div>
      <div class="site-leads-toolbar-field">
        <input-date
          elem-id="siteLeadsFiltroDe"
          v-model="filtroDe"
          :label="t('De')"
          data-testid="site-leads-de-filter"
        />
      </div>
      <div class="site-leads-toolbar-field">
        <input-date
          elem-id="siteLeadsFiltroAte"
          v-model="filtroAte"
          :label="t('Até')"
          data-testid="site-leads-ate-filter"
        />
      </div>
    </div>

    <site-leads-table
      ref="siteLeadsTableRef"
      :filtros="filtros"
      @view="handleDetail"
      @formularios="registraFormularios">
    </site-leads-table>

    <!-- SiteLeadsModal is centralized in ModalManager.vue - opened via eventBus -->
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import SiteLeadsTable from './SiteLeadsTable.vue';
import SelectList from '../../inputs/SelectList.vue';
import InputDate from '../../inputs/InputDate.vue';
import { eventBus } from '../../eventBus';
// SiteLeadsModal is centralized in ModalManager.vue
import { log } from '../../utils/DebugLogger';
import type { SiteLeads_ListaInput } from '../../contracts/SiteLeads_ListaInput';

type LeadId = number | string;

interface SiteLeadsTableInstance {
  loadData: () => void | Promise<void>;
}

withDefaults(defineProps<{
  translations?: Record<string, unknown>;
}>(), {
  translations: () => ({})
});

const { t } = useI18n();

const siteLeadsTableRef = ref<SiteLeadsTableInstance | null>(null);

const filtroFormulario = ref<string>('');
const filtroDe = ref<string>('');
const filtroAte = ref<string>('');

/**
 * The form slugs come from the rows themselves, never from a list held here:
 * the form definition lives in the site's tenant pack and a copy of it in this
 * service would go stale the day a brand adds a form. Accumulated across loads
 * so filtering by one form does not erase the others from the picker.
 */
const formulariosVistos = ref<string[]>([]);

// The panel must OPEN unfiltered. SelectList's auto-select deliberately SKIPS
// the blank placeholder and picks the first real option, which would silently
// narrow the list on mount — so the filter turns it off and the blank first
// option is what "no filter" selects back to.
const formularioOptions = computed(() => [
  { ID: '', Name: t('Todos') },
  ...formulariosVistos.value.map((slug) => ({ ID: slug, Name: slug }))
]);

const filtros = computed<SiteLeads_ListaInput>(() => ({
  formulario: filtroFormulario.value || undefined,
  de: filtroDe.value || undefined,
  ate: filtroAte.value || undefined
}));

const registraFormularios = (slugs: string[]): void => {
  const uniao = new Set([...formulariosVistos.value, ...slugs]);
  formulariosVistos.value = Array.from(uniao).sort();
};

const loadData = async (): Promise<void> => {
  log('SiteLeadsPage', 'loadData', 'Reloading data');
  if (siteLeadsTableRef.value) {
    await siteLeadsTableRef.value.loadData();
  }
};

const handleDetail = (leadId?: LeadId): void => {
  log('SiteLeadsPage', 'handleDetail', 'Opening lead detail via eventBus', leadId);
  if (leadId === undefined) return;
  eventBus.emit('modal.site-leads.open', { id: leadId, action: 'detail' });
};

onMounted(() => {
  log('SiteLeadsPage', 'mounted', 'Component mounted');
});

defineExpose({
  loadData,
  handleDetail
});
</script>

<style scoped>
.site-leads-toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 16px;
}

.site-leads-toolbar-field {
  min-width: 220px;
}
</style>
